import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/layouts/PageContainer";
import { PageHeader } from "@/layouts/PageHeader";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  ClipboardCheck,
  ChevronLeft,
  AlertCircle,
  Check,
  X,
  BookOpen,
  ArrowRight,
  Eye,
  Pencil,
} from "lucide-react";

function Avatar({ name, tone = "primary" }) {
  const initials = (name || "?").slice(0, 2).toUpperCase();
  const toneClass =
    tone === "info"
      ? "bg-info-bg text-info-fg"
      : "bg-primary/10 text-primary";
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold uppercase shrink-0 ${toneClass}`}>
      {initials}
    </div>
  );
}

function PermissionTag({ permission }) {
  const isEdit = permission === "edit";
  const Icon = isEdit ? Pencil : Eye;
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-[0.7rem] font-medium text-muted-foreground">
      <Icon size={11} />
      {isEdit ? "Can edit" : "View only"}
    </span>
  );
}

function ApprovalCard({ request, busy, onApprove, onDeny }) {
  const requesterName = request.requesterId?.name || "A teacher";
  const targetName = request.targetUserId?.name || "another teacher";
  const targetEmail = request.targetUserId?.email;
  const folderLabel =
    request.reportGroupId?.name ||
    [
      request.reportGroupId?.grade && `Grade ${request.reportGroupId.grade}`,
      request.reportGroupId?.schoolYear,
    ]
      .filter(Boolean)
      .join(" · ") ||
    "a Report Folder";

  return (
    <div className="bg-card border border-border rounded-2xl px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={14} className="text-primary shrink-0" />
        <span className="text-sm font-semibold text-foreground truncate">
          {folderLabel}
        </span>
        <span className="ml-auto">
          <PermissionTag permission={request.permission} />
        </span>
      </div>

      <div className="flex items-center gap-3 py-2">
        <Avatar name={requesterName} tone="primary" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            Requester
          </p>
          <p className="text-sm font-medium text-foreground truncate">
            {requesterName}
          </p>
        </div>

        <ArrowRight size={14} className="text-muted-foreground/60 shrink-0 mx-1" />

        <Avatar name={targetName} tone="info" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            Sharing with
          </p>
          <p className="text-sm font-medium text-foreground truncate">
            {targetName}
          </p>
          {targetEmail && (
            <p className="text-[11px] text-muted-foreground truncate">
              {targetEmail}
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-4 pt-3 border-t border-border">
        <Button
          size="sm"
          className="gap-1.5 h-8"
          disabled={busy}
          onClick={onApprove}
        >
          <Check size={13} />
          Approve
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-8 text-muted-foreground hover:text-destructive hover:border-destructive/40"
          disabled={busy}
          onClick={onDeny}
        >
          <X size={13} />
          Deny
        </Button>
      </div>
    </div>
  );
}

export default function ManageReportFolderSharing() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/share-requests`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load share requests");
      }
      setRequests(await res.json());
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useAbortableLoader(load);

  const handleAction = async (requestId, action) => {
    setActionInProgress(requestId + action);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/share-requests/${requestId}/${action}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Failed to ${action} request`);
      }
      setRequests((prev) => prev.filter((r) => r._id !== requestId));
      toast.success(action === "approve" ? "Request approved." : "Request denied.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="h-12 w-2/3 bg-muted animate-pulse rounded mb-8" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-2xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Link
          to="/schools"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft size={14} />
          My School
        </Link>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>

      <PageHeader
        title="Sharing Approvals"
        subtitle="Approve or deny when a teacher requests to share a Report Folder with a colleague. Requests appear here because your school's sharing policy is set to Restricted."
      />

      {requests.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No pending approvals"
          description="When a teacher requests to share a Report Folder, the request will appear here for your review."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/schools/report-folder-shares">View active sharing</Link>
            </Button>
          }
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-4">
            {requests.length} pending request{requests.length !== 1 ? "s" : ""}
          </p>
          <div className="space-y-3">
            {requests.map((req) => (
              <ApprovalCard
                key={req._id}
                request={req}
                busy={actionInProgress?.startsWith(req._id)}
                onApprove={() => handleAction(req._id, "approve")}
                onDeny={() => handleAction(req._id, "deny")}
              />
            ))}
          </div>
        </>
      )}
    </PageContainer>
  );
}
